import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'
import type {
  Breakdown,
  Condition,
  FieldCatalogResponse,
  PlainMetric,
  ResultPage,
  ResultRow,
} from 'features/charts-v2/types/yogiql'
import {
  deriveSentimentMode,
  resolveYogiQLNames,
  STANDARD_FILTER_FIELD,
  toYogiQLFilters,
  withYogiQLVersion,
} from 'features/charts-v2/utils'
import {
  type FilterOption,
  type FilterOptionSelect,
  type FilterOptionValue,
  FilterTypes,
} from 'features/filters/types'
import { ALIAS_FOR_NULL_VALUES } from 'src/constants/filter-values'

/** The body `/client/yogiql-filter-options` takes: a filter-picker query. */
export type YogiQLFilterOptionsRequest = {
  yogiql: string
  metrics: PlainMetric[]
  breakdowns?: Breakdown[]
  filters: Condition[]
  sort?: { by: PlainMetric; dir: 'desc' }[]
}

/** How a control's result rows become the picker's option for the field. */
type RowsShape = 'values' | 'polarity' | 'stars' | 'range'

/** What the route answers a picker query with. */
export type YogiQLFilterOptionsResult = {
  rows: ResultRow[]
  page: ResultPage
}

export type FilterControlQuery = {
  request: YogiQLFilterOptionsRequest
  shape: RowsShape
}

type PanelFilter = ChartPanel['filters'][number]

const RECORDS: PlainMetric = { fn: 'count', field: 'record' }
const SENTENCES: PlainMetric = { fn: 'count', field: 'sentence' }

// The panel filter fields one picker control writes, both of which faceting
// leaves out: sentiment's two modes, and the rating's range and record type.
const CONTROL_FIELDS: Record<string, string[]> = {
  sentiment: ['sentiment', 'post_sentiment'],
  post_sentiment: ['sentiment', 'post_sentiment'],
  score: ['score', 'post_score'],
  post_score: ['score', 'post_score'],
}

const DATE_FIELD = 'create_time'

const counts = (
  metric: PlainMetric,
  breakdown: Breakdown,
  filters: Condition[],
): YogiQLFilterOptionsRequest =>
  withYogiQLVersion({
    metrics: [metric],
    breakdowns: [{ ...breakdown, include_null: true }],
    filters,
    sort: [{ by: metric, dir: 'desc' as const }],
  })

const range = (
  field: string,
  filters: Condition[],
): YogiQLFilterOptionsRequest =>
  withYogiQLVersion({
    metrics: [
      { fn: 'min' as const, field },
      { fn: 'max' as const, field },
    ],
    filters,
  })

const selection = (filters: PanelFilter[], field: string): PanelFilter | null =>
  filters.find((f) => f.field === field) ?? null

/**
 * The YogiQL query that counts one picker control's values under the panel's
 * other filters, or why the control stays on the legacy route. When
 * ``facet`` (the picker), the control's own filters (and, for the date
 * control, the date range) are left out first, so the picker shows counts for
 * values that are not selected. Theme and dataset values carry uuids the
 * legacy options supply, so those controls, and any without a YogiQL field,
 * stay on the legacy route. Pure.
 */
export function filterControlQuery(
  field: string,
  panel: ChartPanel,
  catalog: FieldCatalogResponse,
  filterOptions: FilterOption[] | undefined,
  facet = true,
): FilterControlQuery | string {
  const own = CONTROL_FIELDS[field] ?? [field]
  const faceted: ChartPanel = facet
    ? {
        ...panel,
        filters: panel.filters.filter((f) => !own.includes(f.field)),
        dateRange:
          field === DATE_FIELD ? { startDate: 0, endDate: 0 } : panel.dateRange,
      }
    : panel
  const filters = toYogiQLFilters(
    faceted,
    catalog,
    resolveYogiQLNames(faceted, filterOptions),
  )
  if (typeof filters === 'string') return filters

  if (own.includes('sentiment')) {
    const mode = deriveSentimentMode(
      selection(panel.filters, 'sentiment'),
      selection(panel.filters, 'post_sentiment'),
    )
    // Review mode counts records by their average score; sentence mode
    // counts sentences, since a record has many sentence scores.
    const request =
      mode === 'record'
        ? counts(RECORDS, { field: 'post_sentiment', fn: 'polarity' }, filters)
        : counts(SENTENCES, { field: 'sentiment', fn: 'polarity' }, filters)
    return { request, shape: 'polarity' }
  }
  if (own.includes('score')) {
    const stars = { field: 'rating', fn: 'star' } as const
    return { request: counts(RECORDS, stars, filters), shape: 'stars' }
  }
  if (field === DATE_FIELD) {
    return { request: range('date', filters), shape: 'range' }
  }
  const standard = STANDARD_FILTER_FIELD[field]
  if (standard) {
    return {
      request: counts(RECORDS, { field: standard }, filters),
      shape: 'values',
    }
  }
  const custom = catalog.fields.find((f) => f.custom && f.name === field)
  if (custom?.shape === 'categorical') {
    return {
      request: counts(RECORDS, { field: custom.name }, filters),
      shape: 'values',
    }
  }
  if (custom?.shape === 'numeric') {
    return { request: range(custom.name, filters), shape: 'range' }
  }
  return `'${field}' has no YogiQL filter control yet`
}

const POLARITY_LABEL: Record<string, string> = {
  negative: 'Negative',
  neutral: 'Neutral',
  positive: 'Positive',
}

const label = (
  value: ResultRow['category'][number],
  shape: RowsShape,
): string => {
  if (value === null) return ALIAS_FOR_NULL_VALUES
  const text = String(value)
  return shape === 'polarity' ? (POLARITY_LABEL[text] ?? text) : text
}

const count = (row: ResultRow): number => Number(row.values[0] ?? 0)

/**
 * The control's values the way v2 merges them: every value the hub's cached
 * list holds, counted where the rows have it and 0 where they don't, then any
 * counted value the cache has not caught up with. A cached list that is empty
 * means the hub has too many values to list, so the control lists none and the
 * picker takes typed values. Past the route's cap the rows are a top slice, so
 * a missing value is not a 0 and the rows stand alone.
 */
const mergeValues = (
  counted: FilterOptionValue[],
  cached: FilterOptionValue[] | undefined,
  hasNext: boolean,
): FilterOptionValue[] => {
  if (cached === undefined || hasNext) return counted
  if (cached.length === 0) return []
  const counts = new Map(counted.map((v) => [v.value, v.n_posts]))
  const listed = new Set(cached.map((v) => v.value))
  return [
    ...cached.map((v) => ({ ...v, n_posts: counts.get(v.value) ?? 0 })),
    ...counted.filter((v) => !listed.has(v.value)),
  ]
}

/**
 * The picker's option for ``field`` from the route's result, in the shape the
 * legacy options give it, so every reader of filter options keeps working.
 * ``base`` is the hub's entry for the field from the cached page-load options,
 * for what the rows do not carry: its label, description, bounds and full list
 * of values, and the hub's own brands and products the picker groups the Brand
 * and Product values by. Pure.
 */
export function toFilterOption(
  field: string,
  shape: RowsShape,
  { rows, page }: YogiQLFilterOptionsResult,
  base: FilterOption | undefined,
): FilterOption {
  const basis = {
    alias: base?.alias ?? field,
    field,
    model: base?.model,
    description: base?.description,
    disabled: base?.disabled,
  }
  if (shape === 'values' || shape === 'polarity') {
    // A stub for a select field carries the membership lists but no filter_type.
    const select = base as Partial<FilterOptionSelect> | undefined
    const option: FilterOptionSelect = {
      ...basis,
      customer_brands: select?.customer_brands,
      customer_products: select?.customer_products,
      filter_type: FilterTypes.SELECT,
      values: mergeValues(
        rows.map((row) => ({
          value: label(row.category[0] ?? null, shape),
          n_posts: count(row),
          identifiers: [],
        })),
        select?.values,
        page.has_next,
      ),
    }
    return option
  }
  if (shape === 'stars') {
    const rated = rows.filter((r) => r.category[0] !== null && count(r) > 0)
    const stars = rated.map((r) => Number(r.category[0]))
    const unrated = rows.find((r) => r.category[0] === null)
    const curMin = stars.length ? Math.min(...stars) : undefined
    const curMax = stars.length ? Math.max(...stars) : undefined
    const bounds = base && 'min' in base ? base : undefined
    return {
      ...basis,
      filter_type: FilterTypes.WINDOW,
      min: bounds?.min ?? curMin ?? 1,
      max: bounds?.max ?? curMax ?? 5,
      step: bounds && 'step' in bounds ? bounds.step : 1,
      value_type: bounds?.value_type ?? 0,
      cur_min: curMin,
      cur_max: curMax,
      Null: unrated ? count(unrated) : 0,
    }
  }
  const [low, high] = (rows[0]?.values ?? []) as (number | null)[]
  const bounds = base && 'min' in base ? base : undefined
  const curMin = low ?? undefined
  const curMax = high ?? undefined
  if (field === DATE_FIELD) {
    return {
      ...basis,
      filter_type: FilterTypes.DATE,
      min: bounds?.min ?? curMin ?? 0,
      max: bounds?.max ?? curMax ?? 0,
      value_type: bounds?.value_type ?? 0,
      cur_min: curMin,
      cur_max: curMax,
    }
  }
  return {
    ...basis,
    filter_type: FilterTypes.WINDOW,
    min: bounds?.min ?? curMin ?? 0,
    max: bounds?.max ?? curMax ?? 0,
    step: bounds && 'step' in bounds ? bounds.step : 1,
    value_type: bounds?.value_type ?? 0,
    cur_min: curMin,
    cur_max: curMax,
  }
}
